/** Sprint 5-d + E4 · AI 對話全螢幕 layout（脫離 MainLayout·Claude-style 沉浸）。

App.tsx 以 PrivateRoute 包此 group（login guard）。E4 §4.1 route guard：聊天模式可用性改由
「AI 聊天助手」訂閱 + 使用者未被停用決定（useChatModeAvailable·來源 GET /api/system/my-modes）——
不再看 role_rights ee_ai_chat 或 enabled_ui_modes。不可用 → redirect /home + 提示。
內容全螢幕 100vh + Scope 9 chat 專屬色系 ThemeProvider。ChatPage 自渲染 sidebar + 對話區。
*/
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import ErrorBoundary from '../../../components/ErrorBoundary';
import UpdateBanner from '../../../components/UpdateBanner';
import { useAuth } from '../../../contexts/AuthContext';
import { useChatModeAvailable } from '../../../hooks/useChatModeAvailable';
import { useMessage } from '../../../contexts/MessageContext';
import { ChatThemeProvider } from './ChatThemeContext';

const AIChatLayout: React.FC = () => {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { isAuthLoading, chatUnavailableReason } = useAuth();
  const available = useChatModeAvailable();
  const { showError } = useMessage();
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (isAuthLoading || checked) return;
    setChecked(true);
    if (!available) {
      // E5 §6-1：被停用顯 ERR390025，未訂閱顯 ERR230010
      // lint-msg-allow: 依停用原因二選一·兩者皆 9 碼 sys_message_code
      showError(chatUnavailableReason === 'blocked' ? 'ERR390025' : 'ERR230010');
      nav('/home', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading]);

  if (isAuthLoading) {
    return <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress /></Box>;
  }
  if (!available) {
    return null;   // 導頁進行中
  }

  return (
    <ChatThemeProvider>
      {/* RWD：100dvh 避免手機網址列遮住底部·不支援時退回 100vh（@supports 才是真退回值·陣列是斷點） */}
      <Box sx={{
        height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' },
        overflow: 'hidden', bgcolor: 'background.default',
        display: 'flex', flexDirection: 'column',
      }}>
        <UpdateBanner />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ErrorBoundary key={pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </ChatThemeProvider>
  );
};

export default AIChatLayout;
